// Deliverable Detail Dialog Component
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import FileUpload from '../common/FileUpload';
import api from '../../services/api';
import { 
  Upload, 
  FileText, 
  Mail, 
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  PauseCircle,
  XCircle,
  Pencil,
  Save,
  Bell,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDateInput } from '../../hooks/useDateInput';
import HipNumberEmailDialog from './HipNumberEmailDialog';
import ShipInNoteEmailDialog from './ShipInNoteEmailDialog';
import SellerThankYouEmailDialog from './SellerThankYouEmailDialog';
import QuestionnaireEmailDialog from './QuestionnaireEmailDialog';
import SaleAuthorizationEmailDialog from './SaleAuthorizationEmailDialog';
import SaleAuthorizationSignDialog from './SaleAuthorizationSignDialog';
import ReminderEmailDialog from './ReminderEmailDialog';
import ConsignmentContractDialog from './ConsignmentContractDialog';
import ConsignmentContractEmailDialog from './ConsignmentContractEmailDialog';
import EmailSendHistory from '../shared/EmailSendHistory';
import PublishToPortalSection from './PublishToPortalSection';

const DeliverableDetailDialog = ({ 
  open, 
  onClose, 
  deliverable, 
  deliverableType,
  tenantId,
  horseId, // Horse ID to fetch files from
  deliverableTypeKey, // The actual type key (e.g., 'coggins', 'eva')
  onUpdate,
  onRefresh, // Add onRefresh callback to reload deliverable data
  sale, // Add sale prop to get deadlines
  isReadOnly // Add isReadOnly prop to disable editing for archived opportunities
}) => {
  const [editing, setEditing] = useState(false);
  // Get current user ID for internal signer check
  const currentUserId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id || ''; } catch { return ''; } })();
  const [hipEmailOpen, setHipEmailOpen] = useState(false);
  const [shipInEmailOpen, setShipInEmailOpen] = useState(false);
  const [sellerThankYouEmailOpen, setSellerThankYouEmailOpen] = useState(false);
  const [reminderEmailOpen, setReminderEmailOpen] = useState(false);
  const [questionnaireEmailOpen, setQuestionnaireEmailOpen] = useState(false);
  const [generatingQuestionnaire, setGeneratingQuestionnaire] = useState(false);
  const [authEmailOpen, setAuthEmailOpen] = useState(false);
  const [contractEmailOpen, setContractEmailOpen] = useState(false);
  const [generatingAuth, setGeneratingAuth] = useState(false);
  const [authSignOpen, setAuthSignOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [contractForceRegenerate, setContractForceRegenerate] = useState(false);
  const [fileRefreshKey, setFileRefreshKey] = useState(0);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [formData, setFormData] = useState({
    nickname: deliverable?.nickname || '',
    version: deliverable?.version || '',
    deadline: deliverable?.deadline || '',
    responsible_party: deliverable?.responsible_party || '',
    responsible_party_type: deliverable?.responsible_party_type || '',
    responsible_party_email: deliverable?.responsible_party_email || '',
    status: deliverable?.status || 'not_started',
    received_from: deliverable?.received_from || '',
    received_date: deliverable?.received_date || '',
    submitted_by: deliverable?.submitted_by || '',
    submitted_to: deliverable?.submitted_to || '',
    submitted_date: deliverable?.submitted_date || '',
    amount_due: deliverable?.amount_due || '',
    paid: deliverable?.paid || false,
    scheduled_date: deliverable?.scheduled_date || '',
    scheduled_location: deliverable?.scheduled_location || '',
    notes: deliverable?.notes || ''
  });
  
  // State for responsible party options
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingResponsibleParties, setLoadingResponsibleParties] = useState(false);
  
  // State for selecting from horse files
  const [selectFromHorseDialogOpen, setSelectFromHorseDialogOpen] = useState(false);
  const [horseFiles, setHorseFiles] = useState([]);
  const [loadingHorseFiles, setLoadingHorseFiles] = useState(false);

  // Define which deliverable types have deadlines from the sale
  const saleDeadlineTypes = [
    'coggins', 'eva', 'jockey_club_papers', 'health_certificate',
    'stallion_service_certificate', 'sale_authorization', 'breeding_soundness_exam',
    'sale_company_contract', 'pedigree_proof', 'entry_fee'
  ];
  const hasSaleDeadline = saleDeadlineTypes.includes(deliverableTypeKey);
  
  // Get deadline from sale if applicable
  const getSaleDeadline = () => {
    if (!hasSaleDeadline || !sale) return null;
    
    const deadlineMap = {
      'coggins': sale.coggins_deadline,
      'eva': sale.eva_deadline,
      'jockey_club_papers': sale.jockey_club_deadline,
      'health_certificate': sale.health_certificate_deadline,
      'stallion_service_certificate': sale.stallion_service_cert_deadline,
      'sale_authorization': sale.authorized_agent_form_deadline,
      'breeding_soundness_exam': sale.breeding_soundness_exam_deadline,
      'sale_company_contract': sale.sales_company_contract_deadline,
      'pedigree_proof': sale.pedigree_proof_deadline || sale.pedigree_proof_property_line_deadline,
      'entry_fee': sale.first_500_entry_fee_deadline || sale.entry_deadline,
    };
    
    const deadline = deadlineMap[deliverableTypeKey];
    
    // Convert ISO date to YYYY-MM-DD format for input field
    if (deadline) {
      try {
        const date = new Date(deadline);
        return date.toISOString().split('T')[0];
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  

  // Fetch responsible party options when dialog opens
  useEffect(() => {
    if (open) {
      fetchResponsiblePartyOptions();
    }
  }, [open]);
  
  const fetchResponsiblePartyOptions = async () => {
    setLoadingResponsibleParties(true);
    try {
      // Fetch users
      const usersRes = await api.get('/users');
      setUsers(usersRes.data || []);
      
      // Fetch accounts (CRM entities with crm_class=account)
      const accountsRes = await api.get('/crm-entities', { params: { crm_class: 'account' } });
      setAccounts(accountsRes.data || []);
      
      // Fetch contacts would require knowing which entity's contacts to fetch
      // For now, we'll leave contacts empty and can enhance later if needed
      setContacts([]);
    } catch (error) {
      console.error('Error fetching responsible party options:', error);
    } finally {
      setLoadingResponsibleParties(false);
    }
  };

  const saleDeadline = getSaleDeadline();
  
  // Use sale deadline if available, otherwise use deliverable deadline
  const displayDeadline = hasSaleDeadline && saleDeadline ? saleDeadline : formData.deadline;

  const deadlineRef = useDateInput(displayDeadline);
  const receivedDateRef = useDateInput(formData.received_date);
  const submittedDateRef = useDateInput(formData.submitted_date);
  const scheduledDateRef = useDateInput(formData.scheduled_date);

  const handleSave = async () => {
    try {
      await onUpdate(deliverable.id, formData);
      setEditing(false);
      // Toast will be shown by parent component after checking stage advancement
    } catch (error) {
      toast.error('Failed to update deliverable');
    }
  };

  const handleFileUpload = async (fileUrl, fileName) => {
    try {
      // Update deliverable with file info
      const updateData = {
        file_url: fileUrl,
        file_name: fileName,
        uploaded_at: new Date().toISOString()
      };
      
      // Auto-update status from "not_started" to "in_progress"
      if (deliverable?.status === 'not_started') {
        updateData.status = 'in_progress';
      }
      
      await onUpdate(deliverable.id, updateData);
      toast.success('File uploaded successfully');
      
      // Trigger automatic expiry check for applicable deliverables
      const deliverableType = deliverable?.deliverable_type_horse || deliverable?.deliverable_type;
      const isOwner = Boolean(deliverable?.deliverable_type && !deliverable?.deliverable_type_horse);
      
      if (['coggins', 'eva', 'health_certificate', 'breeding_soundness_exam'].includes(deliverableType)) {
        try {
          const expiryResponse = await api.post('/deliverable-expiry/auto-check', null, {
            params: {
              deliverable_id: deliverable.id,
              is_owner_deliverable: isOwner
            }
          });
          
          if (expiryResponse.data?.is_expired) {
            toast.warning(`Document expired: ${expiryResponse.data.expiry_description}`);
          } else if (expiryResponse.data?.checked) {
            // expiry check passed — no action needed
          }
          
          // Refresh parent data to show updated expiry status
          if (onRefresh) {
            await onRefresh();
          }
        } catch (expiryError) {
          console.error('Expiry check failed:', expiryError);
          // Don't show error to user - this is a background check
        }
      }
      
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      // Clear file info from deliverable
      await onUpdate(deliverable.id, {
        file_url: null,
        file_name: null,
        uploaded_at: null
      });
      toast.success('File removed from deliverable');
    } catch (error) {
      toast.error('Failed to update deliverable');
    }
  };

  const fetchHorseFiles = async () => {
    if (!horseId) {
      toast.error('No horse linked to this deliverable');
      return;
    }
    
    setLoadingHorseFiles(true);
    setSelectFromHorseDialogOpen(true);
    
    try {
      const response = await api.get('/files', { params: { horse_id: horseId } });
      setHorseFiles(response.data || []);
    } catch (error) {
      console.error('Error fetching horse files:', error);
      toast.error('Failed to load horse files');
    } finally {
      setLoadingHorseFiles(false);
    }
  };

  const handleSelectHorseFile = async (file) => {
    try {
      const fileUrl = `/files/${file.id}`;
      // Use description if available, otherwise use original filename
      const fileName = file.description || file.original_filename;
      const isOwner = Boolean(deliverable?.deliverable_type && !deliverable?.deliverable_type_horse);
      
      // Update the file record to link it to this deliverable
      await api.put(`/files/${file.id}/link-deliverable`, {
        sale_deliverable_id: !isOwner ? deliverable.id : null,
        sale_owner_deliverable_id: isOwner ? deliverable.id : null
      });
      
      // Update the deliverable with file info
      await handleFileUpload(fileUrl, fileName);
      
      setSelectFromHorseDialogOpen(false);
      toast.success('File linked to deliverable');
    } catch (error) {
      console.error('Error linking file:', error);
      toast.error('Failed to link file');
    }
  };

  const handleCreateDocument = (docType) => {
    if (deliverableTypeKey === 'consignment_contract') {
      // If contract already generated, still open dialog (it will show existing version)
      setContractDialogOpen(true);
      return;
    }
    toast.info(`${docType} - Coming soon`);
  };

  const handleSendEmail = (emailType) => {
    if (deliverableTypeKey === 'hip_number_email') {
      setHipEmailOpen(true);
    } else if (deliverableTypeKey === 'ship_in_note_email') {
      setShipInEmailOpen(true);
    } else if (deliverableTypeKey === 'seller_thank_you') {
      setSellerThankYouEmailOpen(true);
    } else {
      toast.info(`Send ${emailType} - Coming soon`);
    }
  };

  const handleSendReminderInApp = async () => {
    if (!deliverable?.id) return;
    setSendingReminder(true);
    try {
      await import('../../services/api').then(({ default: api }) =>
        api.post('/deliverable-reminders/send', {
          deliverable_ids: [deliverable.id],
          send_in_app: true,
          send_email: false,
        })
      );
      toast.success('Reminder sent');
    } catch (e) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleRequestQuestionnaire = async () => {
    if (!deliverable?.id) return;
    setGeneratingQuestionnaire(true);
    try {
      await import('../../services/api').then(({ default: api }) =>
        api.post(`/questionnaires/generate/${deliverable.id}`)
      );
      toast.success('Questionnaire generated — preparing email...');
      setFileRefreshKey(k => k + 1);
      onRefresh?.();
      setQuestionnaireEmailOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate questionnaire');
    } finally {
      setGeneratingQuestionnaire(false);
    }
  };

  const handleGenerateQuestionnaire = async () => {
    if (!deliverable?.id) return;
    setGeneratingQuestionnaire(true);
    try {
      await import('../../services/api').then(({ default: api }) =>
        api.post(`/questionnaires/generate/${deliverable.id}`)
      );
      toast.success(deliverable?.questionnaire_file_id ? 'Questionnaire regenerated' : 'Questionnaire generated');
      setFileRefreshKey(k => k + 1);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate questionnaire');
    } finally {
      setGeneratingQuestionnaire(false);
    }
  };

  const handleGenerateAuth = async () => {
    if (!deliverable?.id) return;
    // If a version already exists, just open the email dialog
    if (deliverable.authorization_file_id) {
      setAuthEmailOpen(true);
      return;
    }
    // First time — generate V1
    setGeneratingAuth(true);
    try {
      const res = await api.post(`/sale-authorizations/generate/${deliverable.id}`);
      toast.success(`Authorization generated (${res.data.horse_count} horse${res.data.horse_count !== 1 ? 's' : ''}) — preparing email...`);
      setFileRefreshKey(k => k + 1);
      onRefresh?.();
      setAuthEmailOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate authorization');
    } finally {
      setGeneratingAuth(false);
    }
  };

  const handleRegenerateAuth = async () => {
    if (!deliverable?.id) return;
    setGeneratingAuth(true);
    try {
      const res = await api.post(`/sale-authorizations/generate/${deliverable.id}`);
      toast.success(`New authorization version generated (${res.data.version_label})`);
      setFileRefreshKey(k => k + 1);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to regenerate authorization');
    } finally {
      setGeneratingAuth(false);
    }
  };

  const statusOptions = [
    { value: 'not_started', label: 'Not Started', icon: Circle, color: 'bg-gray-100 text-gray-700' },
    { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'bg-blue-100 text-blue-700' },
    { value: 'on_hold', label: 'On Hold', icon: PauseCircle, color: 'bg-yellow-100 text-yellow-700' },
    { value: 'completed', label: 'Completed', icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-700' },
  ];

  const currentStatus = statusOptions.find(s => s.value === formData.status);

  // Document types that can be system-generated
  const systemDocTypes = [
    'hip_number_email',
    'ship_in_note_email',
    'consignment_contract',
    'sale_authorization',
    'questionnaire'
  ];

  const canGenerateDoc = systemDocTypes.includes(deliverable?.deliverable_type_horse);

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            {deliverableType?.label || 'Deliverable Details'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            {/* Name (read-only) */}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={deliverableType?.label || '-'} disabled className="bg-muted" />
            </div>

            {/* Nickname */}
            <div className="space-y-2">
              <Label>Nickname</Label>
              <Input
                value={formData.nickname}
                onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                placeholder="Optional nickname"
                disabled={!editing}
                className={!editing ? 'bg-muted' : ''}
              />
            </div>

            {/* Deadline */}
            <div className="space-y-2">
              <Label>Deadline {hasSaleDeadline && '(set by Sale)'}</Label>
              <Input
                type="date"
                ref={deadlineRef}
                value={displayDeadline}
                onChange={(e) => !hasSaleDeadline && setFormData({...formData, deadline: e.target.value})}
                disabled={!editing || hasSaleDeadline}
                className={!editing || hasSaleDeadline ? 'bg-muted' : (displayDeadline ? 'date-filled' : '')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Responsible Party */}
            <div className="space-y-2">
              <Label>Responsible Party</Label>
              {editing ? (
                <Select 
                  value={formData.responsible_party} 
                  onValueChange={(value) => {
                    setFormData({
                      ...formData, 
                      responsible_party: value,
                      responsible_party_type: value.startsWith('user_') ? 'user' : 
                                             value.startsWith('account_') ? 'account' : 
                                             value.startsWith('contact_') ? 'contact' : 'other',
                      responsible_party_email: value === 'other' ? formData.responsible_party_email : ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select responsible party..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Users</SelectLabel>
                      {users.map(user => (
                        <SelectItem key={user.id} value={`user_${user.id}`}>
                          {user.first_name} {user.last_name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Accounts</SelectLabel>
                      {accounts.map(account => (
                        <SelectItem key={account.id} value={`account_${account.id}`}>
                          {account.system_name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    {contacts.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Contacts</SelectLabel>
                        {contacts.map(contact => (
                          <SelectItem key={contact.id} value={`contact_${contact.id}`}>
                            {contact.value}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    <SelectGroup>
                      <SelectLabel>Other</SelectLabel>
                      <SelectItem value="other">Other (Enter Email)</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={(() => {
                    // Display the responsible party name based on type
                    if (!formData.responsible_party) return '';
                    if (formData.responsible_party === 'other') return formData.responsible_party_email || 'Other';
                    if (formData.responsible_party.startsWith('user_')) {
                      const user = users.find(u => `user_${u.id}` === formData.responsible_party);
                      return user ? `${user.first_name} ${user.last_name}` : formData.responsible_party;
                    }
                    if (formData.responsible_party.startsWith('account_')) {
                      const account = accounts.find(a => `account_${a.id}` === formData.responsible_party);
                      return account ? account.system_name : formData.responsible_party;
                    }
                    return formData.responsible_party;
                  })()}
                  disabled={true}
                  className="bg-muted"
                />
              )}
            </div>

            {/* Email field for "Other" option */}
            {editing && formData.responsible_party === 'other' && (
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.responsible_party_email}
                  onChange={(e) => setFormData({...formData, responsible_party_email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
            )}

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              {editing ? (
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(opt => {
                      const StatusIcon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <StatusIcon className="w-4 h-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex items-center gap-2">
                  {currentStatus && (
                    <>
                      {React.createElement(currentStatus.icon, { className: 'w-4 h-4' })}
                      <Badge className={currentStatus.color}>{currentStatus.label}</Badge>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Receipt Information */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Received From</Label>
              <Input
                value={formData.received_from}
                onChange={(e) => setFormData({...formData, received_from: e.target.value})}
                placeholder="Enter name/company"
                disabled={!editing}
                className={!editing ? 'bg-muted' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>Received Date</Label>
              <Input
                type="date"
                ref={receivedDateRef}
                value={formData.received_date}
                onChange={(e) => setFormData({...formData, received_date: e.target.value})}
                disabled={!editing}
                className={!editing ? 'bg-muted' : (formData.received_date ? 'date-filled' : '')}
              />
            </div>
            
            <div></div>
          </div>

          {/* Submission Information */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Submitted By</Label>
              <Input
                value={formData.submitted_by}
                onChange={(e) => setFormData({...formData, submitted_by: e.target.value})}
                placeholder="Enter name"
                disabled={!editing}
                className={!editing ? 'bg-muted' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>Submitted To</Label>
              <Input
                value={formData.submitted_to}
                onChange={(e) => setFormData({...formData, submitted_to: e.target.value})}
                placeholder="Enter recipient"
                disabled={!editing}
                className={!editing ? 'bg-muted' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>Submitted Date</Label>
              <Input
                type="date"
                ref={submittedDateRef}
                value={formData.submitted_date}
                onChange={(e) => setFormData({...formData, submitted_date: e.target.value})}
                disabled={!editing}
                className={!editing ? 'bg-muted' : (formData.submitted_date ? 'date-filled' : '')}
              />
            </div>
          </div>

          {/* Amount Due and Paid - Only for Entry Fee */}
          {deliverable?.deliverable_type_horse === 'entry_fee' && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>Amount Due</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount_due}
                  onChange={(e) => setFormData({...formData, amount_due: e.target.value})}
                  placeholder="0.00"
                  disabled={!editing}
                  className={!editing ? 'bg-muted' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label>Paid</Label>
                {editing ? (
                  <div className="flex items-center space-x-2 h-9">
                    <Checkbox
                      checked={formData.paid}
                      onCheckedChange={(checked) => setFormData({...formData, paid: checked})}
                    />
                    <span className="text-sm">Mark as paid</span>
                  </div>
                ) : (
                  <Input value={formData.paid ? 'Yes' : 'No'} disabled className="bg-muted" />
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={3}
              disabled={!editing}
              className={!editing ? 'bg-muted' : ''}
            />
          </div>

          {/* Scheduled Data Section - Only for Image, Video, Xrays, Scope */}
          {['image', 'video', 'xrays', 'scope'].includes(deliverable?.deliverable_type_horse) && (
            <div className="pt-4 border-t">
              <h3 className="font-serif text-base font-semibold mb-4">Scheduled Data</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Scheduled Date</Label>
                  <Input
                    type="date"
                    ref={scheduledDateRef}
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    disabled={!editing}
                    className={!editing ? 'bg-muted' : (formData.scheduled_date ? 'date-filled' : '')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Scheduled Location</Label>
                  <Input
                    value={formData.scheduled_location}
                    onChange={(e) => setFormData({...formData, scheduled_location: e.target.value})}
                    placeholder="Enter location"
                    disabled={!editing}
                    className={!editing ? 'bg-muted' : ''}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Edit Controls — below Notes, no separator */}
          {!isReadOnly && (
            <div className="flex justify-between items-center pt-2">
              {!editing ? (
                <Button variant="ghost" size="icon" onClick={() => setEditing(true)}>
                  <Pencil className="w-4 h-4" />
                </Button>
              ) : (
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} className="bg-primary hover:bg-primary/90 text-white">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t">
            <p className="text-sm font-medium">Actions</p>
            {deliverableTypeKey === 'consignment_contract' && (
              <>
                {deliverable?.contract_internally_signed ? (
                  <div className="w-full space-y-3">
                    {/* Email + Publish side by side */}
                    <div className="flex gap-2 flex-wrap items-start">
                      {deliverable?.emailed ? (
                        <EmailSendHistory
                          sends={deliverable.email_sends?.length > 0 ? deliverable.email_sends : [{ sent_at: deliverable.emailed_at, sent_by: deliverable.emailed_by, sent_to: deliverable.emailed_to, eml_file_id: deliverable.emailed_eml_file_id }]}
                          onResend={() => setContractEmailOpen(true)}
                          emlFilename="contract_email.eml"
                        />
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setContractEmailOpen(true)}
                          className="bg-primary hover:bg-primary/90 text-white"
                        >
                          <Mail className="w-4 h-4 mr-2" /> Email Contract to Owner
                        </Button>
                      )}
                      <PublishToPortalSection
                        deliverableId={deliverable.id}
                        ownerName={deliverable.owner_entity_name || deliverable.owner_crm_entity_id}
                        publishedAt={deliverable.published_to_portal ? deliverable.published_at : null}
                        publishedBy={deliverable.published_by}
                        onRefresh={onRefresh}
                      />
                    </div>
                  </div>
                ) : deliverable?.contract_status === 'awaiting_internal_signature' ? (
                  currentUserId && deliverable?.contract_signatory_user_id && currentUserId === deliverable.contract_signatory_user_id ? (
                    /* Current user IS the signatory — offer direct signing */
                    <Button
                      size="sm"
                      onClick={() => setContractDialogOpen(true)}
                      className="bg-primary hover:bg-primary/90 text-white"
                      data-testid="sign-contract-now-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Sign Contract Now
                    </Button>
                  ) : (
                    /* Different user — show waiting banner with nudge */
                    <div className="border rounded-md p-3 bg-yellow-50 text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600 shrink-0" />
                        Awaiting internal signature from {deliverable.contract_signatory_name || 'signatory'}
                      </div>
                      <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={async () => {
                        try {
                          await api.post(`/consignment-contracts/nudge-signatory/${deliverable.id}`);
                          toast.success('Reminder sent to signatory');
                        } catch { toast.error('Failed to send reminder'); }
                      }}>
                        <Bell className="w-3 h-3 mr-1" /> Nudge
                      </Button>
                    </div>
                  )
                ) : !deliverable?.contract_file_id ? (
                  <Button 
                    size="sm" 
                    onClick={() => handleCreateDocument('Contract')}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Create Contract
                  </Button>
                ) : null}
                {deliverable?.contract_file_id && (
                  <div>
                    <Button variant="outline" size="sm" onClick={() => { setContractForceRegenerate(true); setContractDialogOpen(true); }}>
                      <FileText className="w-4 h-4 mr-2" /> Regenerate Contract
                    </Button>
                  </div>
                )}
              </>
            )}
            {deliverableTypeKey === 'sale_authorization' && (
              <>
                {deliverable?.is_tenant_owner ? (
                  /* === Tenant-Owner Authorization: internal signing flow === */
                  <>
                    {deliverable?.executed_file_id ? (
                      /* Already executed */
                      <div className="border rounded-md p-3 bg-green-50/60 space-y-1" data-testid="signed-authorization-card">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                          <span className="text-sm font-medium">{deliverable.file_name || 'Signed Sale Authorization'}</span>
                        </div>
                        {deliverable.executed_at && (
                          <p className="text-xs text-muted-foreground pl-6">
                            Executed {deliverable.executed_by ? `by ${deliverable.executed_by}` : ''} on {new Date(deliverable.executed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    ) : deliverable?.contract_status === 'awaiting_internal_signature' ? (
                      /* Sent for internal signature */
                      <div className="border rounded-md p-3 bg-yellow-50 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600 shrink-0" />
                        <p className="text-sm">Awaiting internal signature from <strong>{deliverable.contract_signatory_name || 'signatory'}</strong></p>
                        <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs"
                          onClick={async () => {
                            try {
                              await api.post(`/sale-authorizations/approve/${deliverable.id}`);
                              toast.success('Reminder sent');
                              onRefresh?.();
                            } catch { toast.error('Failed to send reminder'); }
                          }}>
                          Nudge Signatory
                        </Button>
                      </div>
                    ) : deliverable?.authorization_file_id ? (
                      /* Generated, ready to approve for internal signature */
                      <>
                        <Button size="sm"
                          className="bg-primary hover:bg-primary/90 text-white"
                          onClick={async () => {
                            try {
                              await api.post(`/sale-authorizations/approve/${deliverable.id}`);
                              toast.success('Authorization sent for internal signature');
                              onRefresh?.();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
                          }}>
                          <CheckCircle2 className="w-4 h-4 mr-2" /> Approve for Internal Signature
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleRegenerateAuth} disabled={generatingAuth}>
                          {generatingAuth ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                          Regenerate Authorization
                        </Button>
                      </>
                    ) : (
                      /* Not yet generated */
                      <Button size="sm" onClick={handleGenerateAuth} disabled={generatingAuth}
                        className="bg-primary hover:bg-primary/90 text-white">
                        {generatingAuth ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><FileText className="w-4 h-4 mr-2" /> Create Authorization</>}
                      </Button>
                    )}
                  </>
                ) : (
                  /* === Standard Owner Authorization: email flow === */
                  <>
                    {deliverable?.emailed ? (
                      <div className="w-full space-y-2">
                        <EmailSendHistory
                          sends={deliverable.email_sends?.length > 0 ? deliverable.email_sends : [{ sent_at: deliverable.emailed_at, sent_by: deliverable.emailed_by, sent_to: deliverable.emailed_to, eml_file_id: deliverable.emailed_eml_file_id }]}
                          onResend={() => setAuthEmailOpen(true)}
                          emlFilename="authorization_email.eml"
                        />
                      </div>
                    ) : deliverable?.authorization_file_id ? (
                      /* Auth generated but not yet emailed — show Send, not Create */
                      <Button size="sm" onClick={() => setAuthEmailOpen(true)}
                        className="bg-primary hover:bg-primary/90 text-white" data-testid="send-auth-to-owner-btn">
                        <Mail className="w-4 h-4 mr-2" /> Send to Owner
                      </Button>
                    ) : (
                      /* No auth yet — first-time generation */
                      <Button size="sm" onClick={handleGenerateAuth} disabled={generatingAuth}
                        className="bg-primary hover:bg-primary/90 text-white" data-testid="create-auth-btn">
                        {generatingAuth ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><FileText className="w-4 h-4 mr-2" /> Create Authorization</>}
                      </Button>
                    )}
                    {deliverable?.authorization_file_id && (
                      <PublishToPortalSection
                        deliverableId={deliverable.id}
                        ownerName={deliverable.owner_entity_name || deliverable.owner_crm_entity_id}
                        publishedAt={deliverable.published_to_portal ? deliverable.published_at : null}
                        publishedBy={deliverable.published_by}
                        onRefresh={onRefresh}
                      />
                    )}
                    {deliverable?.executed_file_id && (
                      <div className="border rounded-md p-3 bg-green-50/60 space-y-1" data-testid="signed-authorization-card">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                          <span className="text-sm font-medium">{deliverable.file_name || 'Signed Sale Authorization'}</span>
                        </div>
                        {deliverable.executed_at && (
                          <p className="text-xs text-muted-foreground pl-6">
                            Executed {deliverable.executed_by ? `by ${deliverable.executed_by}` : ''} on {new Date(deliverable.executed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    )}
                    {deliverable?.authorization_file_id && (
                      <Button variant="outline" size="sm" onClick={handleRegenerateAuth} disabled={generatingAuth} data-testid="regenerate-auth-btn">
                        {generatingAuth ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                        Regenerate Authorization
                      </Button>
                    )}
                  </>
                )}
              </>
            )}

            {/* For hip/ship/seller-thank-you email types: combined row of Create Email + optional reminder buttons */}
            {['hip_number_email', 'ship_in_note_email', 'seller_thank_you'].includes(deliverableTypeKey) && !deliverable?.emailed && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => handleSendEmail(deliverableTypeKey === 'hip_number_email' ? 'Hip Letter Email' : 'Ship-in Note Email')}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <Mail className="w-4 h-4 mr-2" /> Create Email
                </Button>
                {!isReadOnly && deliverable?.status !== 'completed' && deliverable?.responsible_party && (
                  <>
                    {deliverable?.responsible_party_type === 'user' && (
                      <Button size="sm" variant="outline" onClick={handleSendReminderInApp} disabled={sendingReminder}>
                        {sendingReminder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                        Send Reminder
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setReminderEmailOpen(true)}>
                      <Mail className="w-4 h-4 mr-2" /> Send Reminder Email
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Reminder buttons — for all other deliverable types */}
            {!['hip_number_email', 'ship_in_note_email', 'seller_thank_you'].includes(deliverableTypeKey) &&
              !isReadOnly && deliverable?.status !== 'completed' && deliverable?.responsible_party && (
              <div className="flex gap-2">
                {deliverable?.responsible_party_type === 'user' && (
                  <Button size="sm" variant="outline" onClick={handleSendReminderInApp} disabled={sendingReminder}>
                    {sendingReminder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                    Send Reminder
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setReminderEmailOpen(true)}>
                  <Mail className="w-4 h-4 mr-2" /> Send Reminder Email
                </Button>
              </div>
            )}
            
            {/* Button row: Request Questionnaire (green, leftmost) + Select from Horse Files */}
            {horseId && !deliverable?.deliverable_type && (
              <div className="space-y-2">
                {deliverableTypeKey === 'questionnaire' && (
                  <div className="space-y-3">
                    {/* Row 1: Email + Publish side by side (mirrors contract layout) — only when PDF exists */}
                    {deliverable?.questionnaire_file_id && (
                      <div className="flex gap-2 flex-wrap items-start">
                        {deliverable?.emailed ? (
                          <EmailSendHistory
                            sends={deliverable.email_sends && deliverable.email_sends.length > 0
                              ? deliverable.email_sends
                              : [{ sent_at: deliverable.emailed_at, sent_by: deliverable.emailed_by, sent_to: deliverable.emailed_to, eml_file_id: deliverable.emailed_eml_file_id }]}
                            onResend={() => setQuestionnaireEmailOpen(true)}
                            emlFilename="questionnaire_email.eml"
                          />
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => setQuestionnaireEmailOpen(true)}
                            className="bg-primary hover:bg-primary/90 text-white"
                          >
                            <Mail className="w-4 h-4 mr-2" /> Email Questionnaire to Owner
                          </Button>
                        )}
                        <PublishToPortalSection
                          deliverableId={deliverable.id}
                          publishedAt={deliverable.published_to_portal ? deliverable.published_at : null}
                          publishedBy={deliverable.published_by}
                          onRefresh={onRefresh}
                          publishEndpoint={`/questionnaires/${deliverable.id}/publish-to-portal`}
                          unpublishEndpoint={`/questionnaires/${deliverable.id}/unpublish-from-portal`}
                        />
                      </div>
                    )}
                    {/* Row 2: Create (primary, green) when no PDF; Regenerate (secondary, outline) when PDF exists */}
                    {!deliverable?.questionnaire_file_id ? (
                      <Button
                        size="sm"
                        onClick={handleGenerateQuestionnaire}
                        disabled={generatingQuestionnaire}
                        className="bg-primary hover:bg-primary/90 text-white"
                      >
                        {generatingQuestionnaire
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                          : <><FileText className="w-4 h-4 mr-2" /> Create Questionnaire</>}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleGenerateQuestionnaire}
                        disabled={generatingQuestionnaire}
                      >
                        {generatingQuestionnaire
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regenerating...</>
                          : <><FileText className="w-4 h-4 mr-2" /> Regenerate Questionnaire</>}
                      </Button>
                    )}
                  </div>
                )}
                {!(deliverableTypeKey === 'questionnaire' && deliverable?.questionnaire_file_id) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchHorseFiles}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select from Horse Files
                  </Button>
                )}
              </div>
            )}
            
            {/* File Upload using FileUpload component — hidden for email-only deliverables */}
            {!['hip_number_email', 'ship_in_note_email'].includes(deliverableTypeKey) && (
            <div className="space-y-2">
              <Label className="text-xs">Upload Document/File</Label>
              <FileUpload
                key={`fileupload-${deliverable?.id}-${fileRefreshKey}`}
                tenantId={tenantId}
                entityType={deliverable?.deliverable_type ? 'sale_owner_deliverable' : 'sale_deliverable'}
                entityId={deliverable?.id}
                defaultCategory={(() => {
                  // Map deliverable types to file categories
                  const categoryMap = {
                    'catalogue_page': 'pdf_catalogue_page',
                    'coggins': 'pdf_coggins',
                    'eva': 'pdf_eva',
                    'jockey_club_papers': 'pdf_jockey_club_papers',
                    'entry_fee': 'pdf_invoice',
                    'breeding_soundness_exam': 'pdf_breeding_soundness_exam',
                    'eye_certificate': 'pdf_eye_certificate',
                    'fetal_sexing': 'pdf_fetal_sexing',
                    'health_certificate': 'pdf_health_certificate',
                    'medication_treatment_disclosure': 'pdf_medication_disclosure',
                    'pedigree_proof': 'pdf_pedigree_proof',
                    'questionnaire': 'questionnaire_cba_yearling',
                    'scope': 'pdf_scope',
                    'stallion_service_certificate': 'pdf_stallion_service_cert',
                    'surgery_letter': 'pdf_surgery_letter',
                    'vet_report': 'pdf_vet_report',
                    'xrays': 'pdf_xrays',
                    'video': 'video_other',
                    'image': 'photo',
                    'other': 'other',
                    // Owner deliverable types
                    'sale_company_contract': 'other',
                    'sale_authorization': 'other',
                    'consignment_contract': 'other',
                    'hip_number_email': 'other',
                    'ship_in_note_email': 'other'
                  };
                  return categoryMap[deliverable?.deliverable_type_horse || deliverable?.deliverable_type] || 'other';
                })()}
                categoryReadOnly={true}
                onUploadComplete={(fileUrl, fileName) => handleFileUpload(fileUrl, fileName)}
                onDeleteComplete={(fileId) => handleFileDelete(fileId)}
                compact={true}
              />
            </div>
            )}

            {/* System-generated Document Actions - Specific buttons per type */}
            <div className="space-y-2">
              {/* Entry Fee - Create Invoice */}
              {deliverableTypeKey === 'entry_fee' && (
                <Button 
                  size="sm" 
                  onClick={() => handleCreateDocument('Invoice')}
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" /> Create Invoice
                </Button>
              )}
              
              {/* Hip Number Email */}
              {deliverableTypeKey === 'hip_number_email' && deliverable?.emailed && (
                <div className="w-full">
                  <EmailSendHistory
                    sends={deliverable.email_sends && deliverable.email_sends.length > 0 ? deliverable.email_sends : [{ sent_at: deliverable.emailed_at, sent_by: deliverable.emailed_by, sent_to: deliverable.emailed_to, eml_file_id: deliverable.emailed_eml_file_id }]}
                    onResend={() => handleSendEmail('Hip Letter Email')}
                    emlFilename="hip_number_email.eml"
                  />
                </div>
              )}
              
              {/* Ship-In Note Email */}
              {deliverableTypeKey === 'ship_in_note_email' && deliverable?.emailed && (
                <div className="w-full">
                  <EmailSendHistory
                    sends={deliverable.email_sends && deliverable.email_sends.length > 0 ? deliverable.email_sends : [{ sent_at: deliverable.emailed_at, sent_by: deliverable.emailed_by, sent_to: deliverable.emailed_to, eml_file_id: deliverable.emailed_eml_file_id }]}
                    onResend={() => handleSendEmail('Ship-in Note Email')}
                    emlFilename="ship_in_note_email.eml"
                  />
                </div>
              )}

              {/* Seller Thank You Email */}
              {deliverableTypeKey === 'seller_thank_you' && deliverable?.emailed && (
                <div className="w-full">
                  <EmailSendHistory
                    sends={deliverable.email_sends && deliverable.email_sends.length > 0 ? deliverable.email_sends : [{ sent_at: deliverable.emailed_at, sent_by: deliverable.emailed_by, sent_to: deliverable.emailed_to, eml_file_id: deliverable.emailed_eml_file_id }]}
                    onResend={() => handleSendEmail('Seller Thank You')}
                    emlFilename="seller_thank_you.eml"
                  />
                </div>
              )}
              
              
              {/* Sale Authorization - handled in Actions section above */}
              
              {/* Questionnaire - button moved to row with Select from Horse Files above */}
            </div>
          </div>
        </div>

      </DialogContent>
    </Dialog>

    
    {/* Select from Horse Files Dialog */}
    <Dialog open={selectFromHorseDialogOpen} onOpenChange={setSelectFromHorseDialogOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select from Horse Files</DialogTitle>
        </DialogHeader>
        
        {loadingHorseFiles ? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">Loading files...</p>
          </div>
        ) : horseFiles.length === 0 ? (
          <div className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-20" />
            <p className="text-muted-foreground">No files found on horse profile</p>
            <p className="text-xs text-muted-foreground mt-2">Upload files to the horse's Photos & Files section first</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
            {horseFiles.map(file => (
              <div
                key={file.id}
                className="border rounded-lg p-3 cursor-pointer hover:bg-muted/50 hover:border-primary transition-colors"
                onClick={() => handleSelectHorseFile(file)}
              >
                <div className="aspect-square bg-muted flex items-center justify-center rounded mb-2">
                  {file.mime_type?.startsWith('image/') ? (
                    <img src={`/api/files/${file.id}`} alt={file.description} className="w-full h-full object-cover rounded" />
                  ) : (
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                <p className="text-xs font-medium truncate" title={file.description || file.original_filename}>
                  {file.description || file.original_filename}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {file.category?.replace(/_/g, ' ')}
                </p>
              </div>
            ))}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setSelectFromHorseDialogOpen(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <HipNumberEmailDialog
      open={hipEmailOpen}
      onClose={(sent) => {
        setHipEmailOpen(false);
        if (sent) {
          onRefresh?.();
          onClose?.();
        }
      }}
      deliverableId={deliverable?.id}
    />
    <ShipInNoteEmailDialog
      open={shipInEmailOpen}
      onClose={(sent) => {
        setShipInEmailOpen(false);
        if (sent) {
          onRefresh?.();
          onClose?.();
        }
      }}
      deliverableId={deliverable?.id}
    />
    <SellerThankYouEmailDialog
      open={sellerThankYouEmailOpen}
      onClose={(sent) => {
        setSellerThankYouEmailOpen(false);
        if (sent) { onRefresh?.(); onClose?.(); }
      }}
      deliverableId={deliverable?.id}
    />
    <ReminderEmailDialog
      open={reminderEmailOpen}
      onClose={(sent) => {
        setReminderEmailOpen(false);
        if (sent) onRefresh?.();
      }}
      deliverableId={deliverable?.id}
    />
    <SaleAuthorizationEmailDialog
      open={authEmailOpen}
      onClose={(sent) => { setAuthEmailOpen(false); if (sent) { setFileRefreshKey(k => k + 1); onRefresh?.(); } }}
      deliverableId={deliverable?.id}
      saleId={deliverable?.sale_id}
      ownerId={deliverable?.owner_crm_entity_id}
      onSent={() => { setFileRefreshKey(k => k + 1); onRefresh?.(); }}
    />
    <SaleAuthorizationSignDialog
      open={authSignOpen}
      onClose={(done) => { setAuthSignOpen(false); if (done) { onRefresh?.(); onClose?.(); } }}
      deliverableId={deliverable?.id}
      onSigned={() => onRefresh?.()}
    />
    <QuestionnaireEmailDialog
      open={questionnaireEmailOpen}
      onClose={(sent) => {
        setQuestionnaireEmailOpen(false);
        if (sent) { setFileRefreshKey(k => k + 1); onRefresh?.(); }
      }}
      deliverableId={deliverable?.id}
      onSent={() => { setFileRefreshKey(k => k + 1); onRefresh?.(); }}
    />
    <ConsignmentContractDialog
      open={contractDialogOpen}
      onClose={(done) => { setContractDialogOpen(false); setContractForceRegenerate(false); if (done) { setFileRefreshKey(k => k + 1); onRefresh?.(); } }}
      deliverableId={deliverable?.id}
      onGenerated={() => { setFileRefreshKey(k => k + 1); onRefresh?.(); }}
      forceRegenerate={contractForceRegenerate}
    />
    <ConsignmentContractEmailDialog
      open={contractEmailOpen}
      onClose={(sent) => { setContractEmailOpen(false); if (sent) { setFileRefreshKey(k => k + 1); onRefresh?.(); } }}
      deliverableId={deliverable?.id}
      saleId={deliverable?.sale_id}
      ownerId={deliverable?.owner_crm_entity_id}
      onSent={() => { setFileRefreshKey(k => k + 1); onRefresh?.(); }}
    />
    </>
  );
};

export default DeliverableDetailDialog;
